import { useEffect, useState, type FormEvent } from "react";
import { receivingApi } from "../../shared/receivingApi";
import type { ApiErrorPayload, ApiResult } from "../../types/api";
import type { SupplierOption, Warehouse } from "../../types/receiving";

interface SupplierForm {
  name: string;
  supplier_number: string;
}

interface WarehouseForm {
  name: string;
  code: string;
}

interface LocationForm extends WarehouseForm {
  warehouse_id: number | "";
}

export default function ReceivingSetup() {
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [supplier, setSupplier] = useState<SupplierForm>({ name: "", supplier_number: "" });
  const [warehouse, setWarehouse] = useState<WarehouseForm>({ name: "", code: "" });
  const [location, setLocation] = useState<LocationForm>({ warehouse_id: "", name: "", code: "" });
  const [message, setMessage] = useState("");

  const load = async () => {
    const [supplierResult, warehouseResult] = await Promise.all([
      receivingApi.suppliers(),
      receivingApi.warehouses(),
    ]);
    if (supplierResult.success) setSuppliers(supplierResult.data);
    if (warehouseResult.success) setWarehouses(warehouseResult.data);
  };
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (
    event: FormEvent<HTMLFormElement>,
    action: () => Promise<ApiResult<unknown, ApiErrorPayload>>,
    reset: () => void,
  ) => {
    event.preventDefault();
    setMessage("");
    const result = await action();
    if (result.success) {
      reset();
      setMessage("Saved.");
      load();
    } else
      setMessage(
        result.data?.message ||
          Object.values(result.data?.errors || {}).flat()[0] ||
          "Could not save."
      );
  };

  return (
    <div className="p-6 space-y-5">
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary-600">
          Receiving setup
        </p>
        <h1 className="mt-1 text-2xl font-bold text-gray-900">Suppliers and storage</h1>
        <p className="mt-1 text-sm text-gray-500">
          Define who sends inventory and exactly where received quantities are stored.
        </p>
      </section>
      {message && (
        <p className="rounded-lg bg-gray-100 p-3 text-sm font-medium text-gray-700">{message}</p>
      )}
      <div className="grid gap-5 xl:grid-cols-2">
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <h2 className="text-lg font-semibold">Suppliers</h2>
          <form
            onSubmit={(e) =>
              void submit(
                e,
                () => receivingApi.createSupplier({ ...supplier, supplier_number: supplier.supplier_number || null, status: true }),
                () => setSupplier({ name: "", supplier_number: "" })
              )
            }
            className="mt-4 grid gap-3 sm:grid-cols-2"
          >
            <input
              required
              placeholder="Supplier name"
              value={supplier.name}
              onChange={(e) => setSupplier({ ...supplier, name: e.target.value })}
              className="rounded-lg border-gray-300"
            />
            <input
              placeholder="Supplier number"
              value={supplier.supplier_number}
              onChange={(e) => setSupplier({ ...supplier, supplier_number: e.target.value })}
              className="rounded-lg border-gray-300"
            />
            <button className="rounded-lg bg-primary-600 px-4 py-2 font-semibold text-white sm:col-span-2">
              Add supplier
            </button>
          </form>
          <div className="mt-5 divide-y">
            {suppliers.map((s) => (
              <div key={s.value} className="flex justify-between py-3">
                <span className="font-medium">{s.label}</span>
                <span className="font-mono text-sm text-gray-500">{s.supplier_number || "—"}</span>
              </div>
            ))}
          </div>
        </section>
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <h2 className="text-lg font-semibold">Warehouses</h2>
          <form
            onSubmit={(e) =>
              void submit(
                e,
                () => receivingApi.createWarehouse({ ...warehouse, status: true }),
                () => setWarehouse({ name: "", code: "" })
              )
            }
            className="mt-4 grid gap-3 sm:grid-cols-2"
          >
            <input
              required
              placeholder="Warehouse name"
              value={warehouse.name}
              onChange={(e) => setWarehouse({ ...warehouse, name: e.target.value })}
              className="rounded-lg border-gray-300"
            />
            <input
              required
              placeholder="Code"
              value={warehouse.code}
              onChange={(e) => setWarehouse({ ...warehouse, code: e.target.value })}
              className="rounded-lg border-gray-300"
            />
            <button className="rounded-lg bg-primary-600 px-4 py-2 font-semibold text-white sm:col-span-2">
              Add warehouse
            </button>
          </form>
          <div className="mt-5 divide-y">
            {warehouses.map((w) => (
              <div key={w.id} className="py-3">
                <div className="flex justify-between">
                  <span className="font-medium">{w.name}</span>
                  <span className="font-mono text-sm text-gray-500">{w.code}</span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {w.locations?.map((l) => l.code).join(" · ") || "No locations yet"}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <h2 className="text-lg font-semibold">Add warehouse location</h2>
        <form
          onSubmit={(e) =>
            void submit(
              e,
              () =>
                receivingApi.createLocation(location.warehouse_id, {
                  name: location.name,
                  code: location.code,
                  status: true,
                }),
              () => setLocation({ warehouse_id: "", name: "", code: "" })
            )
          }
          className="mt-4 grid gap-3 md:grid-cols-4"
        >
          <select
            required
            value={location.warehouse_id}
            onChange={(e) => setLocation({ ...location, warehouse_id: Number(e.target.value) || "" })}
            className="rounded-lg border-gray-300"
          >
            <option value="">Warehouse</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
          <input
            required
            placeholder="Location name"
            value={location.name}
            onChange={(e) => setLocation({ ...location, name: e.target.value })}
            className="rounded-lg border-gray-300"
          />
          <input
            required
            placeholder="Location code"
            value={location.code}
            onChange={(e) => setLocation({ ...location, code: e.target.value })}
            className="rounded-lg border-gray-300"
          />
          <button className="rounded-lg bg-primary-600 px-4 py-2 font-semibold text-white">
            Add location
          </button>
        </form>
      </section>
    </div>
  );
}
